import { useSession } from 'components/auth/useSession'
import { usePecField } from 'components/form'
import { useConexaoHorusLazyQuery, useConsultaEstoqueHorusLazyQuery } from 'graphql/hooks.generated'
import { ConexaoHorusQuery, DisponibilidadeHorusEnum } from 'graphql/types.generated'
import { usePubSub } from 'hooks/pubsub/usePubSub'
import { useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-final-form'
import { MetaRoot } from 'util/metaPath'
import { v4 as uuidv4 } from 'uuid'

import { MedicamentoFormModel } from '../model-prescricao'

interface ResultadoConsultaHorusType {
  uuid: string
  validadeConexao: DisponibilidadeHorusEnum
  unidadesSaudeConsulta: ConsultaHorusItemType[]
}

export interface ConsultaHorusItemType {
  quantidadeDisponivel: number
  disponibilidade: DisponibilidadeHorusEnum
  unidadeSaude: {
    id: string
    nome?: string
  }
}

interface UsePrescricaoHorusIntegrationProps {
  name: MetaRoot<MedicamentoFormModel>
}

interface UsePrescricaoHorusIntegrationResult {
  horusData: ConsultaHorusItemType[] | null
  statusConsulta: DisponibilidadeHorusEnum | undefined
  isHorusLoading: boolean
  loadingConexaoInternet: boolean
  executeConexaoQuery: () => void
  setHorusData: React.Dispatch<React.SetStateAction<ConsultaHorusItemType[] | null>>
  handleMedicamentoChange: () => void
}

export const usePrescricaoHorusIntegration = (
  props: Readonly<UsePrescricaoHorusIntegrationProps>
): UsePrescricaoHorusIntegrationResult => {
  const { name } = props
  const form = useForm<MedicamentoFormModel>()
  const { mutators } = form
  const { input: principioAtivoComboInput } = usePecField({
    name: name.principioAtivoCombo,
    subscription: { value: true },
  })
  const principioAtivoComboValue = principioAtivoComboInput.value
  const principioAtivo = principioAtivoComboValue?.principioAtivo?.nome
  const codigoCatMat = principioAtivoComboValue?.codigoRename ?? principioAtivoComboValue?.codigoCatMat
  const { data: session } = useSession()
  const [consultaEstoqueHorusLazyQuery] = useConsultaEstoqueHorusLazyQuery()
  const [executeConexaoQuery, { loading: loadingConexaoInternet }] = useConexaoHorusLazyQuery({
    onCompleted: (dataConexao) => setStatusConexao(dataConexao),
  })

  const [hashConsultaHorus, setHashConsultaHorus] = useState<string>()
  const [horusData, setHorusData] = useState<ConsultaHorusItemType[] | null>(null)
  const [statusConsulta, setStatusConsulta] = useState<DisponibilidadeHorusEnum>()
  const [isHorusLoading, setIsHorusLoading] = useState(false)
  const [statusConexao, setStatusConexao] = useState<ConexaoHorusQuery>()

  useEffect(() => {
    executeConexaoQuery()
  }, [executeConexaoQuery])

  useEffect(() => {
    if (!statusConexao) {
      return
    }

    if (!statusConexao.conexao?.internet.habilitado) {
      setStatusConsulta(DisponibilidadeHorusEnum.SEM_CONEXAO)
      return
    }

    if (!statusConexao.conexao?.horus.habilitado) {
      setStatusConsulta(DisponibilidadeHorusEnum.DESABILITADO)
      return
    }

    if (principioAtivo) {
      const hash = uuidv4()
      setHashConsultaHorus(hash)
      setIsHorusLoading(true)
      consultaEstoqueHorusLazyQuery({
        variables: {
          filter: {
            principioAtivo: principioAtivo,
            codCatMat: codigoCatMat,
            uuid: hash,
          },
        },
      })
    }
  }, [codigoCatMat, consultaEstoqueHorusLazyQuery, principioAtivo, statusConexao])

  usePubSub<ResultadoConsultaHorusType>({
    topic: `/consulta-horus/${session.profissional.usuario.id}`,
    onMessage: (responseBody) => {
      if (responseBody?.uuid === hashConsultaHorus) {
        setStatusConsulta(responseBody.validadeConexao)
        setIsHorusLoading(false)
        setHorusData(responseBody.unidadesSaudeConsulta)
        mutators.setValue(name.disponibilidade.alias, responseBody?.unidadesSaudeConsulta[0]?.disponibilidade)
      }
    },
  })

  const handleMedicamentoChange = useCallback(() => horusData !== null && setHorusData(null), [horusData])

  return {
    horusData,
    statusConsulta,
    isHorusLoading,
    loadingConexaoInternet,
    executeConexaoQuery,
    setHorusData,
    handleMedicamentoChange,
  }
}
